/**
 * Confirm a Site DN on site (21 #9 / 17 #8): who received it, their signature (foundation SignaturePad — finger,
 * pen, mouse or "Type instead") and/or a photo of the signed paper DN, and a note. Sends
 * POST /stock/site-dns/{id}/receive; the DN then reads "Received by … · date time" everywhere and prints with
 * the signature. A phone bottom sheet (centred sheet on wider screens).
 */
import { useEffect, useRef, useState } from 'react';
import { CheckCheck } from 'lucide-react';
import api from '@/lib/api';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { ActionBar } from '@/components/ui/action-bar';
import { SignaturePad } from '@/components/ui/signature-pad';
import { FileDropzone } from '@/components/ui/file-dropzone';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { notify } from '@/components/ui/notify';
import { getErrorMessage } from '@/lib/errors';
import { documentLabel, formatQty, plural } from './inventoryModel';

const PHOTO_MAX_BYTES = 8 * 1024 * 1024;

export default function ReceiveDnSheet({ open, onOpenChange, group, dnId, projectName = '', defaultName = '', itemDescriptions = {}, onReceived }) {
  const pad = useRef(null);
  const [name, setName] = useState(defaultName);
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [signed, setSigned] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(defaultName);
    setNotes('');
    setPhotos([]);
    setSigned(false);
    setError('');
  }, [defaultName, open]);

  if (!group) return null;
  const label = documentLabel(group);

  const submit = async () => {
    const cleanName = name.replace(/\s+/g, ' ').trim();
    if (!cleanName) { setError('Type the name of the person who received the items'); return; }
    const signature = signed ? await pad.current?.toBlob() : null;
    const photo = photos[0] || null;
    if (!signature && !photo) { setError('Sign above, or add a photo of the signed DN'); return; }
    setBusy(true);
    setError('');
    try {
      const form = new FormData();
      form.append('received_by_name', cleanName);
      form.append('notes', notes.trim());
      if (signature) form.append('signature', signature, 'signature.png');
      if (photo) form.append('photo', photo, photo.name || 'signed-dn.jpg');
      const res = await api.post(`/stock/site-dns/${encodeURIComponent(dnId)}/receive`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      notify.success(`${label} received by ${res.data?.dn_received_by_name || cleanName}`, { description: projectName ? `Confirmed for ${projectName}` : undefined });
      onOpenChange(false);
      onReceived?.(res.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not confirm the DN'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={`Confirm ${label} received`}
      description={`${projectName ? `${projectName} · ` : ''}${plural(group.lines, 'line')} · ${formatQty(group.qtyTotal)} pcs`}
      testId="receive-dn-sheet"
      footer={(
        <ActionBar
          columns={1}
          actions={[{ key: 'confirm', label: 'Confirm receipt', icon: CheckCheck, variant: 'primary', busy, onSelect: submit, testId: 'receive-dn-submit' }]}
          ariaLabel="Confirm"
        />
      )}
    >
      <ul className="mb-3 divide-y divide-line-2 rounded-xl border border-line px-3 text-[13px]">
        {group.movements.slice(0, 6).map((movement) => (
          <li key={movement.id} className="flex items-center gap-2 py-1.5">
            <span className="shrink-0 font-mono text-[12.5px] text-ink">{movement.part_number || '—'}</span>
            <span className="min-w-0 flex-1 truncate text-ink-3">{movement.description || itemDescriptions[movement.part_number] || ''}</span>
            <b className="shrink-0 tabular-nums">{formatQty(Math.abs(Number(movement.qty) || 0))}</b>
          </li>
        ))}
        {group.movements.length > 6 ? <li className="py-1.5 text-[12px] text-ink-3">+ {plural(group.movements.length - 6, 'more line')}</li> : null}
      </ul>
      <label className="block">
        <span className="mb-1 block text-[13px] font-semibold text-ink-2">Received by <span className="text-status-red-ink">*</span></span>
        <Input value={name} onChange={(event) => setName(event.target.value)} maxLength={100} placeholder="Name of the person on site" className="h-11 text-[15px]" data-testid="receive-dn-name" />
      </label>
      <div className="mt-3">
        <SignaturePad ref={pad} label="Signature of the receiver" height={170} onChange={({ empty }) => setSigned(!empty)} testId="receive-dn-signature" />
      </div>
      <div className="mt-3">
        <span className="mb-1 block text-[13px] font-semibold text-ink-2">Or a photo of the signed DN</span>
        <FileDropzone
          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
          multiple={false}
          maxSize={PHOTO_MAX_BYTES}
          capture="environment"
          compact
          label="Take a photo or choose one"
          hint="JPG, PNG or WebP · up to 8 MB"
          onFilesChange={setPhotos}
          testId="receive-dn-photo"
        />
      </div>
      <label className="mt-3 block">
        <span className="mb-1 block text-[13px] font-semibold text-ink-2">Note <span className="font-normal text-ink-3">· optional</span></span>
        <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={500} rows={2} placeholder="e.g. One box damaged — 1 pc short" data-testid="receive-dn-notes" />
      </label>
      {error ? <p className="mt-2 text-[13px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
    </BottomSheet>
  );
}
